// Google Sheets API integration utility - Temporary no-cors version
// This uses Google Apps Script as a proxy to avoid CORS issues

class GoogleSheetsService {
  constructor() {
    // Replace with your Google Apps Script web app URL
    this.scriptUrl = import.meta.env.VITE_GOOGLE_SCRIPT_URL || 'https://script.google.com/macros/s/YOUR_SCRIPT_ID/exec';
  }

  // Submit quotation data to Google Sheets
  async submitQuotation(data) {
    try {
      const payload = {
        action: 'submitQuotation',
        data: {
          timestamp: new Date().toISOString(),
          name: data.name,
          email: data.email,
          phone: data.phone,
          message: data.message,
          service: data.service || 'Custom Quote',
          location: data.location || 'Not specified',
          status: 'PENDING'
        }
      };

      const response = await fetch(this.scriptUrl, {
        method: 'POST',
        mode: 'no-cors', // Using no-cors to avoid CORS issues
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      // Since we're using no-cors, we can't read the response
      // But we can assume success if no error is thrown
      return { success: true, message: 'Quote submitted successfully!' };
    } catch (error) {
      console.error('Google Sheets submission error:', error);
      throw new Error('Failed to submit quote to Google Sheets');
    }
  }

  // Submit appointment data to Google Sheets
  async submitAppointment(data) {
    try {
      const payload = {
        action: 'submitAppointment',
        data: {
          timestamp: new Date().toISOString(),
          name: data.fullName,
          phone: data.mobileNumber,
          location: data.location,
          type: 'Consultation',
          status: 'PENDING'
        }
      };

      const response = await fetch(this.scriptUrl, {
        method: 'POST',
        mode: 'no-cors', // Using no-cors to avoid CORS issues
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      // Since we're using no-cors, we can't read the response
      // But we can assume success if no error is thrown
      return { success: true, message: 'Appointment submitted successfully!' };
    } catch (error) {
      console.error('Google Sheets appointment submission error:', error);
      throw new Error('Failed to submit appointment to Google Sheets');
    }
  }

  // Get quotations from Google Sheets
  async getQuotations() {
    try {
      // Temporarily return empty array since we can't read response with no-cors
      // This will be fixed once Google Apps Script is updated with CORS support
      console.log('Note: Admin panel data fetching requires CORS-enabled Google Apps Script');
      return [];
    } catch (error) {
      console.error('Google Sheets get quotations error:', error);
      return [];
    }
  }

  // Get appointments from Google Sheets
  async getAppointments() {
    try {
      // Temporarily return empty array since we can't read response with no-cors
      // This will be fixed once Google Apps Script is updated with CORS support
      console.log('Note: Admin panel data fetching requires CORS-enabled Google Apps Script');
      return [];
    } catch (error) {
      console.error('Google Sheets get appointments error:', error);
      return [];
    }
  }

  // Update quotation status
  async updateQuotationStatus(quotationId, newStatus) {
    try {
      const payload = {
        action: 'updateQuotationStatus',
        quotationId: quotationId,
        newStatus: newStatus
      };

      const response = await fetch(this.scriptUrl, {
        method: 'POST',
        mode: 'no-cors', // Using no-cors to avoid CORS issues
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      // Since we're using no-cors, we can't read the response
      // But we can assume success if no error is thrown
      return { success: true, message: 'Status updated successfully!' };
    } catch (error) {
      console.error('Google Sheets update status error:', error);
      throw new Error('Failed to update status in Google Sheets');
    }
  }

  // Update appointment status
  async updateAppointmentStatus(appointmentId, newStatus) {
    try {
      const payload = {
        action: 'updateAppointmentStatus',
        appointmentId: appointmentId,
        newStatus: newStatus
      };

      const response = await fetch(this.scriptUrl, {
        method: 'POST',
        mode: 'no-cors', // Using no-cors to avoid CORS issues
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      // Since we're using no-cors, we can't read the response
      // But we can assume success if no error is thrown
      return { success: true, message: 'Status updated successfully!' };
    } catch (error) {
      console.error('Google Sheets update appointment status error:', error);
      throw new Error('Failed to update appointment status in Google Sheets');
    }
  }
}

// Create and export a singleton instance
export const googleSheetsService = new GoogleSheetsService();
